import type { ReactNode, FC } from 'react';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import {
  default as OnScrollBottom,
  DISTANCE_UNIT,
} from '@wix/da-shared-react/pkg/OnScrollBottom';
import Pagination from '@wix/da-shared-react/pkg/Pagination';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import ArrowDown from '@wix/da-ds/pkg/Icons/16x16/ArrowDown';
import { sanitizeHref } from '@wix/da-url';
import {
  DISTANCE_FROM_BOTTOM_TO_FETCH_MORE,
  ONSCROLLBOTTOM_THROTTLE,
} from '../constants';

import s from './PagingWrapper.scss';

export interface Props {
  streamId: string;
  isInfiniteScroll: boolean;
  isDuperbrowseActive: boolean;
  hasMore: boolean;
  hasLess: boolean;
  isFetching: boolean;
  nextPageUrl: string;
  prevPageUrl: string;
  error?: string;
  customEndOfResults?: ReactNode;
  fetchMore: (streamId: string) => void;
  children?: ReactNode;
}

const PagingWrapper: FC<Props> = ({
  streamId,
  children,
  isFetching,
  hasMore,
  hasLess,
  isDuperbrowseActive,
  isInfiniteScroll,
  nextPageUrl,
  prevPageUrl,
  error,
  customEndOfResults,
  fetchMore,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { isLoggedIn } = useContext(SessionContext);
  const shouldRequireInfiniteScrollStart =
    isInfiniteScroll && !isLoggedIn && isMobile;
  const [mobileInfScrollStarted, setMobileInfScrollStarted] = useState(false);

  const handleScrollToBottom = useCallback(() => {
    if (isFetching || !hasMore || isDuperbrowseActive || error) {
      return;
    }
    fetchMore(streamId);
  }, [fetchMore, hasMore, isDuperbrowseActive, isFetching, error, streamId]);

  const currentPage = hasLess ? 2 : 1;
  const totalPages = hasMore ? currentPage + 1 : currentPage;
  return (
    <>
      {children}
      {isFetching && <LoadingIndicator />}
      {!isFetching &&
        !hasMore &&
        (customEndOfResults ?? (
          <div className={s['end-of-results']}>
            {t(['facetPage.endResults'])}
          </div>
        ))}
      {!isInfiniteScroll && (
        <div className={s['paged']}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            showPageNumbers={false}
            getPageUrl={page =>
              page > currentPage ? nextPageUrl : prevPageUrl
            }
          />
        </div>
      )}
      {hasMore && shouldRequireInfiniteScrollStart && (
        <div
          className={cn(
            s['start-infscroll-button'],
            mobileInfScrollStarted && s['hidden']
          )}
        >
          <SolidButton
            icon={ArrowDown}
            postfixIcon={ArrowDown}
            variant={'approval-green'}
            size={'large'}
            href={sanitizeHref(nextPageUrl)}
            onClick={e => {
              e.preventDefault();
              setMobileInfScrollStarted(true);
            }}
          >
            {t('facetPage.startInfiniteScroll')}
          </SolidButton>
        </div>
      )}
      {isInfiniteScroll &&
        (mobileInfScrollStarted || !shouldRequireInfiniteScrollStart) && (
          <OnScrollBottom
            checkOnMount
            checkOnUpdate={hasMore}
            onScrolledToBottom={handleScrollToBottom}
            distanceToBottom={DISTANCE_FROM_BOTTOM_TO_FETCH_MORE}
            distanceUnit={DISTANCE_UNIT.PX}
            timeout={ONSCROLLBOTTOM_THROTTLE}
          />
        )}
    </>
  );
};
PagingWrapper.displayName = 'PagingWrapper';

export default PagingWrapper;
